import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import ParkingCard from "../components/ParkingCard";
import Logo from "../components/Logo";
import { Search, MapPin, Shield, Clock, Zap, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";

import { API } from "../utils/api";

export default function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [featuredSpots, setFeaturedSpots] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFeaturedSpots();
    seedData();
  }, []);

  const seedData = async () => {
    try {
      await axios.post(`${API}/seed`);
    } catch (error) {
      console.debug("Seed skipped:", error.message);
    }
  };

  const fetchFeaturedSpots = async () => {
    try {
      const response = await axios.get(`${API}/spots/featured`);
      setFeaturedSpots(response.data);
    } catch (error) {
      console.error("Error fetching spots:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/explore?search=${encodeURIComponent(searchQuery)}`);
    } else {
      navigate("/explore");
    }
  };

  const popularLocations = [
    { name: "Centro Ciudad", icon: "🏙️" },
    { name: "Aeropuerto", icon: "✈️" },
    { name: "Estación Tren", icon: "🚂" },
    { name: "Hospital", icon: "🏥" },
  ];

  const features = [
    {
      icon: Shield,
      title: t('home.howItWorks.step1.title'),
      description: t('home.howItWorks.step1.description')
    },
    {
      icon: Clock,
      title: t('home.howItWorks.step2.title'),
      description: t('home.howItWorks.step2.description')
    },
    {
      icon: Zap,
      title: t('home.howItWorks.step3.title'),
      description: t('home.howItWorks.step3.description')
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: "url('https://images.unsplash.com/photo-1761262197606-592bc24df449?crop=entropy&cs=srgb&fm=jpg&q=85')"
          }}
        />
        <div className="hero-overlay absolute inset-0" />
        <div className="absolute inset-0 bg-glow" />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 animate-fadeIn">
              {t('home.hero.title')}
              <span className="text-violet-400"> {t('home.hero.titleHighlight')}</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 animate-fadeIn animate-fadeIn-delay-1">
              {t('home.hero.subtitle')}
            </p>

            {/* Search Box */}
            <form 
              onSubmit={handleSearch}
              className="search-box p-6 rounded-2xl max-w-2xl animate-fadeIn animate-fadeIn-delay-2"
              data-testid="search-form"
            >
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="text"
                    placeholder={t('home.hero.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-12 h-14 bg-[#15121e] border-white/10 text-white placeholder:text-gray-500 rounded-xl"
                    data-testid="search-input"
                  />
                </div>
                <Button 
                  type="submit"
                  className="h-14 px-8 bg-violet-500 hover:bg-violet-600 text-white rounded-xl glow-violet-hover"
                  data-testid="search-submit-btn"
                >
                  <Search className="w-5 h-5 mr-2" />
                  {t('home.hero.searchButton')}
                </Button>
              </div>

              {/* Popular Locations */}
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="text-gray-500 text-sm">Popular:</span>
                {popularLocations.map((loc) => (
                  <button
                    key={loc.name}
                    type="button"
                    onClick={() => {
                      setSearchQuery(loc.name);
                      navigate(`/explore?search=${encodeURIComponent(loc.name)}`);
                    }}
                    className="text-sm text-gray-400 hover:text-violet-400 transition-colors"
                    data-testid={`popular-${loc.name.toLowerCase().replace(' ', '-')}`}
                  >
                    {loc.icon} {loc.name}
                  </button>
                ))}
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-[#15121e]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="p-8 rounded-2xl bg-[#1a1625] border border-white/5 card-hover"
              >
                <div className="w-14 h-14 rounded-xl bg-violet-500/10 flex items-center justify-center mb-6">
                  <feature.icon className="w-7 h-7 text-violet-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Spots Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="text-3xl font-bold text-white mb-2">{t('home.featured.title')}</h2>
              <p className="text-gray-400">{t('home.featured.subtitle')}</p>
            </div>
            <Link 
              to="/explore"
              className="hidden sm:flex items-center gap-2 text-violet-400 hover:text-violet-300 transition-colors"
              data-testid="view-all-spots"
            >
              {t('common.seeAll')}
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="parking-card">
                  <div className="skeleton h-48" />
                  <div className="p-4 space-y-3">
                    <div className="skeleton h-5 w-3/4 rounded" />
                    <div className="skeleton h-4 w-1/2 rounded" />
                    <div className="skeleton h-6 w-1/4 rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : featuredSpots.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredSpots.map((spot) => (
                <ParkingCard key={spot.id} spot={spot} />
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">
                <MapPin className="w-10 h-10 text-gray-500" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">{t('explore.noResults')}</h3>
              <p className="text-gray-400 mb-6">{t('addSpot.subtitle')}</p>
              <Link to="/add-spot">
                <Button className="bg-violet-500 hover:bg-violet-600 text-white">
                  {t('nav.addSpot')}
                </Button>
              </Link>
            </div>
          )}

          <Link 
            to="/explore"
            className="sm:hidden flex items-center justify-center gap-2 mt-8 text-violet-400 hover:text-violet-300 transition-colors"
          >
            {t('common.seeAll')}
            <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-violet-600/20 to-emerald-600/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            {t('home.cta.title') || "¿Tienes una plaza de garaje sin usar?"}
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            {t('home.cta.subtitle') || "Gana dinero extra alquilándola cuando no la necesites. Tú decides cuándo y a qué precio."}
          </p>
          <Link to={user ? "/add-spot" : "/register"}>
            <Button 
              className="h-14 px-10 bg-violet-500 hover:bg-violet-600 text-white text-lg rounded-xl glow-violet-hover"
              data-testid="cta-add-spot"
            >
              {t('home.cta.button') || "Empieza a ganar dinero"}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
